import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Button,
  CircularProgress,
  Container,
  FormHelperText,
  Stack,
  TextField,
  Alert,
  AlertTitle,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Photo } from '@watchduty/camera';
import { Capacitor } from '@capacitor/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { API } from 'api';
import { S3AuthData, UploadImageData } from 'shared/types';
import {
  ReportMedia,
  ReportModerationStatus,
} from 'features/reports/report.types';
import {
  clearPhotoSubmissionDraft,
  getPhotoSubmissionDraft,
  isPhotoDraftRecoverySupported,
  savePhotoSubmissionDraft,
} from 'features/reports/utils/photoSubmissionDraft';
import {
  formatFileUploadKey,
  getGPSDataFromFileAndroid,
  getGPSDataFromFileIOS,
  readFilePath,
  uploadImage,
} from 'shared/utils';
import { useUserPosition } from 'hooks/useUserPosition';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import WarningIcon from '@mui/icons-material/Warning';
import { Loader } from 'components/Loader';
import { TEXTAREA_MAX_ROWS } from '../../../../constants';
import { FilePicker } from './FilePicker';
import { ImagePreview } from './ImagePreview';

type PhotoSubmissionFormProps = {
  geoEventId?: number;
  setSuccess: Dispatch<SetStateAction<boolean>>;
};

type FormValues = {
  photo: File | Blob | null;
  fileType: string;
  message: string;
  lat: number;
  lng: number;
  az: number;
};

type ReportData = {
  media: Partial<ReportMedia>[];
  message: string;
  geoEventId?: number;
  status?: ReportModerationStatus;
};

const submitNewReport = async (reportData: ReportData): Promise<void> => {
  await API.post('/reports/', reportData);
};

const useStyles = makeStyles()((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
    overflowY: 'auto',
  },
  error: {
    margin: '3px 14px 0px',
  },
}));

const initialValues: FormValues = {
  photo: null,
  fileType: '',
  message: '',
  lat: 0,
  lng: 0,
  az: 0,
};

export const PhotoSubmissionForm = (
  props: PhotoSubmissionFormProps,
): JSX.Element => {
  const { geoEventId, setSuccess } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { lat: userLat, lng: userLng } = useUserPosition();
  const location = useLocation();
  const hasHydratedDraftRef = useRef(false);

  const s3AuthQuery = useQuery({
    queryKey: ['gets3Auth'],
    queryFn: () => API.get<S3AuthData>('media/storage_auth/'),
  });

  const uploadImageMutation = useMutation({
    mutationFn: (data: UploadImageData) => uploadImage(data),
  });

  const postReportMutation = useMutation({
    mutationFn: (data: ReportData) => submitNewReport(data),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        photo: Yup.mixed<File | Blob | null>().required(
          t('photoSubmission.form.inputs.photo.required'),
        ),
        fileType: Yup.string().nullable(),
        message: Yup.string()
          .required(t('photoSubmission.form.inputs.description.required'))
          .min(3, t('photoSubmission.form.inputs.description.minError')),
        lat: Yup.number(),
        lng: Yup.number(),
        az: Yup.number(),
      }),
    [t],
  );

  const {
    control,
    // Subscribe to these before any early-return: RHF's formState is a Proxy that only
    // tracks properties accessed during render. If `isValid` isn't read here, it won't
    // update when `shouldValidate` changes.
    formState: { isDirty, isSubmitting, isValid },
    setValue,
    setError,
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  useFormBlocker({
    shouldBlockNavigation: isDirty,
  });

  const handleFilePicked = useCallback(
    async (newFile: File | File[] | Photo): Promise<void> => {
      if (Capacitor.getPlatform() === 'web') {
        // this is from the Web. We get an array, even though we are only handling 1 photo for now
        const webFile = Array.isArray(newFile) ? newFile[0] : newFile;

        if (!('type' in webFile)) return;

        // type is the image mime type ie: 'image/jpeg'
        const imageTypeIndex = 1;
        const parsedFileType = webFile.type.split('/')[imageTypeIndex];

        setValue('fileType', parsedFileType, { shouldDirty: true });
        setValue('photo', webFile, { shouldDirty: true, shouldValidate: true });

        return;
      }

      if ('format' in newFile) {
        // Round-trip complete; the draft saved before launching the camera is no longer needed.
        // Cleared up front so a failed file read below can't leave it behind.
        clearPhotoSubmissionDraft();

        const gpsData =
          Capacitor.getPlatform() === 'ios'
            ? getGPSDataFromFileIOS(newFile)
            : getGPSDataFromFileAndroid(newFile);

        const { bearing, lat = userLat, lng = userLng } = gpsData;

        if (lat && lng) {
          setValue('lat', lat, { shouldDirty: true });
          setValue('lng', lng, { shouldDirty: true });
        }

        // iOS reports a value of -1 when the compass reading is invalid
        if (bearing != null && bearing >= 0) {
          setValue('az', bearing, { shouldDirty: true });
        }

        const res = await readFilePath(newFile);
        if (!res) return;

        setValue('fileType', res.type, { shouldDirty: true });
        setValue('photo', res.file, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [setValue, userLat, userLng],
  );

  const handleBeforeCapture = useCallback((): void => {
    if (!isPhotoDraftRecoverySupported()) return;

    savePhotoSubmissionDraft({
      route: `${location.pathname}${location.search}`,
      message: getValues('message'),
    });
  }, [getValues, location.pathname, location.search]);

  // Rehydrate after a process-kill recovery (success or cancellation; see
  // RestoredPhotoListener). The message is restored whenever a draft exists; the photo only
  // if the camera call actually succeeded. The draft is consumed here so it can't resurface on
  // a later visit (e.g. after the user discards their changes) or leak into another form.
  useEffect(() => {
    if (hasHydratedDraftRef.current) return;
    hasHydratedDraftRef.current = true;

    const draft = getPhotoSubmissionDraft();
    if (!draft) return;
    clearPhotoSubmissionDraft();

    if (draft.message) {
      setValue('message', draft.message, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (draft.photo) {
      handleFilePicked(draft.photo);
    }
  }, [handleFilePicked, setValue]);

  const handleInvalidFiles = (error: string): void => {
    setError('photo', { message: error });
  };

  const handleSubmitReport: SubmitHandler<FormValues> = async (values) => {
    const { photo, fileType, lat, lng, az, message } = values;

    const s3AuthKey = s3AuthQuery.data?.data?.fields.key;
    if (!s3AuthKey) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, {
        cause: 'missing-s3-auth-key',
      });
      return;
    }

    const mediaUrl = formatFileUploadKey({ fileType, s3AuthKey });

    /**
     * Maybe janky but for now we aren't allowing geodata with reports
     * only with Media.
     */
    const reportData: ReportData = {
      message,
      media: [
        {
          lat: lat || null,
          lng: lng || null,
          az: az || null,
          url: mediaUrl,
        },
      ],
      status: 'pending',
      geoEventId,
    };

    try {
      if (!s3AuthQuery.data || !photo) return;

      await uploadImageMutation.mutateAsync({
        media: photo,
        s3AuthData: s3AuthQuery.data,
        s3Key: mediaUrl,
      });

      await postReportMutation.mutateAsync(reportData);

      setSuccess(true);
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  if (s3AuthQuery.isLoading) {
    return <Loader disableBackground />;
  }

  return (
    <Container maxWidth="md" className={classes.container}>
      <Stack
        spacing={2}
        component="form"
        onSubmit={handleSubmit(handleSubmitReport)}
        noValidate
      >
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{ '& .MuiAlert-icon': { mr: 1, alignSelf: 'center' } }}
        >
          <AlertTitle sx={{ m: 0 }}>
            {t('photoSubmission.form.alert')}
          </AlertTitle>
        </Alert>

        <Controller
          name="photo"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            if (!field.value) {
              return (
                <>
                  <FilePicker
                    message={t('photoSubmission.form.inputs.photo.label')}
                    onFilesPicked={handleFilePicked}
                    onFilesInvalid={handleInvalidFiles}
                    onBeforeCapture={handleBeforeCapture}
                    onCaptureCancelled={clearPhotoSubmissionDraft}
                    disabled={isSubmitting}
                    error={fieldState.error?.message}
                    minHeight={392}
                  />
                  {Boolean(fieldState.error?.message) && (
                    <FormHelperText error className={classes.error}>
                      {fieldState.error?.message}
                    </FormHelperText>
                  )}
                </>
              );
            }
            return (
              <ImagePreview
                // @ts-expect-error - name is a valid property
                title={field.value.name}
                data={field.value}
                minHeight={392}
              />
            );
          }}
        />

        <Controller
          name="message"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-message"
                label={t('photoSubmission.form.inputs.description.label')}
                fullWidth
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                multiline
                minRows={4}
                maxRows={TEXTAREA_MAX_ROWS}
                disabled={isSubmitting}
              />
            );
          }}
        />

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('photoSubmission.form.button')
          )}
        </Button>
      </Stack>
    </Container>
  );
};
